/**
 * Single source of truth for how access levels are shown to people.
 *
 * PART F rules:
 *  - "Owner" is displayed as "Owner" everywhere. The stored role value is
 *    still `super_admin`, this is display text only, no permission logic changes.
 *  - Viewer, Editor and Admin keep their names. They are deliberately different
 *    from the job titles used on staff profiles (Practitioner, Nursery Manager
 *    and so on), so nobody confuses what someone's job is with what they are
 *    allowed to do on the platform.
 *  - A role name must never be shown on its own. Always pair it with the short
 *    plain-language line from ROLE_SUMMARY (use `roleWithSummary` for one string,
 *    or the RoleBadge component for a badge with the line beside or behind it).
 */

export type RoleKey = "viewer" | "editor" | "front_desk" | "admin" | "super_admin";

export const ROLE_LABEL: Record<RoleKey, string> = {
  viewer: "Viewer",
  editor: "Editor",
  front_desk: "Front Desk",
  admin: "Admin",
  super_admin: "Owner",
};

/** Short, plain-language meaning of each level. Always shown with the label. */
export const ROLE_SUMMARY: Record<RoleKey, string> = {
  viewer:
    "can sign children in and out, and mark pickups and drop-offs, for their own room",
  editor:
    "everything a Viewer can do, plus can edit child records and the medicine log",
  front_desk:
    "can add children, enrol from requests and archive children, sign children in and out across any room, manage the rota, update a child's session, funding and SEND details, and track staff DBS and PFA renewals. Cannot import spreadsheets, restore archived children, log medicine, complete risk assessments, or access LADO and social care records",
  admin: "full access to run the nursery day to day, cannot access billing",
  super_admin: "full access to everything, including billing and account settings",
};

export const ROLE_ORDER: RoleKey[] = ["viewer", "editor", "front_desk", "admin", "super_admin"];

export const ROLE_BADGE_CLASS: Record<RoleKey, string> = {
  viewer: "bg-muted text-muted-foreground",
  editor: "bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))]",
  front_desk: "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))]",
  admin: "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]",
  super_admin: "bg-primary text-primary-foreground",
};

function key(role: string | null | undefined): RoleKey {
  return (role && role in ROLE_LABEL ? role : "viewer") as RoleKey;
}

export function roleLabel(role: string | null | undefined): string {
  return ROLE_LABEL[key(role)];
}

export function roleSummary(role: string | null | undefined): string {
  return ROLE_SUMMARY[key(role)];
}

/** e.g. "Owner: full access to everything, including billing and account settings" */
export function roleWithSummary(role: string | null | undefined): string {
  const k = key(role);
  return `${ROLE_LABEL[k]}: ${ROLE_SUMMARY[k]}`;
}

export function roleBadgeClass(role: string | null | undefined): string {
  return ROLE_BADGE_CLASS[key(role)];
}

/**
 * Safeguarding lead and successor designations follow the same rule: never a
 * bare badge, always a one-line explanation nearby.
 */
export const DESIGNATION_SUMMARY = {
  dsl: "Designated Safeguarding Lead: the person legally responsible for safeguarding here, so they can see safeguarding records whatever their access level",
  deputy_dsl:
    "Deputy Safeguarding Lead: stands in for the safeguarding lead and sees the same safeguarding records",
  successor:
    "Designated Successor: can be verified and given Owner access if you are ever unavailable, so the nursery is never locked out",
  backup_successor:
    "Backup Successor: second in line if the primary successor cannot be reached",
} as const;
